<template>
    <b-container fluid>
        <b-overlay :show="loading" rounded="sm" opacity="0.3" bg-color="#efefef">
            <b-card>
                <template #header>
                    <div class="d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">해당없음(NA) 격상 목록</h5>
                        <b-button variant="dark" :disabled="loading" @click="run">
                            <b-spinner v-if="loading" small class="mr-1"/>
                            전체 조회 &amp; 다운로드
                        </b-button>
                    </div>
                </template>

                <p class="text-muted f-90 mb-2">
                    hyundai-v2 전 사업장(아래 캠페인)의 결과보고서 데이터를 조회해,
                    <strong>해당없음(NA) 만점 처리로 Code 경영수준이 상향된 케이스</strong>(예: 매우미흡→미흡, 보통→우수)를
                    찾아 xlsx 로 내려받습니다. 답변 자체로는 미달이나 NA 만점으로 등급이 올라간 코드가 대상입니다.
                </p>

                <div class="mb-2">
                    <span class="f-90 mr-2">대상 캠페인:</span>
                    <b-badge v-for="cid in targetCampaigns" :key="cid" variant="secondary" class="mr-1">{{ cid }}</b-badge>
                    <span class="f-80 text-muted ml-1">(테스트 캠페인 550 제외)</span>
                </div>

                <div v-if="progress.total > 0" class="mt-3">
                    <div class="f-90 mb-1">조회 진행률 ({{ progress.done }} / {{ progress.total }} 기업)</div>
                    <b-progress :value="progress.done" :max="progress.total" height="22px">
                        <b-progress-bar :value="progress.done" variant="success">
                            {{ progressPercent }}%
                        </b-progress-bar>
                    </b-progress>
                </div>

                <div v-if="hasRun && !loading" class="mt-3">
                    <b-alert :variant="results.length ? 'warning' : 'success'" show class="mb-2">
                        <strong>{{ results.length }}건</strong>의 격상 케이스가 발견되었습니다.
                        ({{ scannedCompanies }}개 기업 조회<span v-if="errors.length">, {{ errors.length }}건 조회 실패</span>)
                    </b-alert>

                    <div v-if="errors.length" class="f-80 text-danger">
                        <div class="mb-1">조회 실패 목록:</div>
                        <div v-for="(e, i) in errors" :key="i">- {{ e.scope }}: {{ e.reason }}</div>
                    </div>
                </div>
            </b-card>
        </b-overlay>
    </b-container>
</template>

<script>
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import {getRendererCampaignList} from '@shared_mixins/report/report-template';
import {code_level_thresholds, code_localized} from '@shared/mixin/report/raw-data/hyundai_v2_2026';

const LEVEL_RANK = {major: 0, minor: 1, conformance: 2, excellent: 3};
const LEVEL_KO = {major: '매우 미흡', minor: '미흡', conformance: '보통', excellent: '우수', na: '해당 없음'};

// scoring/risk row 들을 sub_code 별로 묶어 codeLevelByCode 와 동일한 item 구조로 환산.
// label(= df_subs_init.label = Q-no) 은 row 에서 그대로 가져온다.
function buildItems (rows) {
    const subGroups = {};
    for (const r of rows) {
        const sc = r.sub_code;
        if (!sc) continue;
        if (!subGroups[sc]) subGroups[sc] = {sub_code: sc, label: '', rows: [], risk: r.risk === true};
        subGroups[sc].rows.push(r);
        if (r.risk === true) subGroups[sc].risk = true;
        if (!subGroups[sc].label && r.label) subGroups[sc].label = r.label;
    }
    return Object.values(subGroups).map(g => {
        const allNA = g.rows.every(r => r.not_applicable === true);
        const satisfied = g.rows.some(r => r.satisfied === true && r.not_applicable !== true);
        const score = g.rows.reduce((s, r) => s + (r.not_applicable ? 0 : (Number(r.score) || 0)), 0);
        const cap = g.rows.reduce((m, r) => Math.max(m, Number(r.cap_score) || 0), 0);
        return {sub_code: g.sub_code, label: g.label, score, cap, satisfied, not_applicable: allNA, is_core: g.risk};
    });
}

// codeLevelByCode 와 동일한 레벨 판정. naAsCap=true 면 NA sub_code 를 만점(cap)으로, false 면 0점으로 가산.
function levelOf (items, lv, naAsCap) {
    if (!items.length) return 'na';
    if (items.every(i => i.not_applicable)) return 'na';
    if (items.some(i => i.is_core && !i.not_applicable && !i.satisfied)) return 'major';
    const total = items.reduce((s, i) => s + (i.not_applicable ? (naAsCap ? (i.cap || 0) : 0) : (i.score || 0)), 0);
    if (lv.excellent && lv.excellent.minScore != null && total >= lv.excellent.minScore) return 'excellent';
    if (lv.conformance && lv.conformance.minScore != null && total >= lv.conformance.minScore) return 'conformance';
    if (lv.minor && lv.minor.minScore != null && total >= lv.minor.minScore) return 'minor';
    return 'major';
}

// /subs 응답(sub_data_contents.result) → sub_code 별 답변 result(예/아니오/모름) 맵. (loadAnswerResults 미러)
function answerResultMapFromSubs (subsData) {
    const map = {};
    (subsData ?? []).forEach(sub => {
        if (!sub || !sub.sub_code) return;
        let sdc = sub.sub_data_contents;
        if (typeof sdc === 'string') {
            try { sdc = JSON.parse(sdc); } catch (e) { sdc = null; }
        }
        map[sub.sub_code] = sdc && sdc.result !== undefined ? sdc.result : null;
    });
    return map;
}

// 답변 result(df_subs) 기준 음성분기(아니오/모름) 행 반환. (negativeAnswerRow 미러)
function negativeAnswerRow (subCode, rows, answerMap) {
    const result = answerMap ? answerMap[subCode] : null;
    if (result == null) return null;
    let needle = null;
    if (result === '모름') needle = 'result==\'모름\'';
    else if (typeof result === 'string' && result.startsWith('아니오')) needle = 'result==\'아니오';
    if (!needle) return null;
    return (rows || []).find(r => r && r.ev_name && r.formula && r.formula.includes(needle)) || null;
}

// 보고서 Code 상세의 bullet 문구(ev_name) 수집 — collectEvNamesForCodeDetail_v2(ko) 미러.
//   rawRows = scoring_pd + risk_pd, level = 화면 표출 등급(NA만점 levelWith).
function collectEvNames (codeId, rawRows, answerMap, level) {
    const matched = rawRows.filter(r => {
        const m = String(r.category3_question_name || '').match(/^([A-Z]-\d+-\d+)/);
        return m && m[1] === codeId;
    });
    const byGroup = {};
    for (const r of matched) {
        if (r.not_applicable === true) continue;
        const sc = r.sub_code || 'default';
        if (!byGroup[sc]) byGroup[sc] = [];
        byGroup[sc].push(r);
    }
    const isBad = level === 'major' || level === 'minor';
    const result = [];
    for (const sc of Object.keys(byGroup)) {
        const subRows = byGroup[sc];
        // 매우미흡/미흡(bad)일 때만 부정 지표 노출. 보통/우수는 충족만 (report collectEvNamesForCodeDetail_v2 미러).
        if (isBad) {
            const neg = negativeAnswerRow(sc, subRows, answerMap);
            if (neg) { if (neg.ev_name) result.push(neg.ev_name); continue; }
            const aniyo = subRows.find(r => r.formula && r.formula.includes('result==\'아니오'));
            const rev = aniyo ? aniyo.ev_reverse_name : '';
            if (rev) { result.push(rev); continue; }
        }
        // score===0 제외 (MLT buildGoodLevelItem 미러). 모름/아니오 음성답변 ev 는 satisfied=true 라도 score=0.
        const sat = subRows.filter(r => r.satisfied === true && Number(r.score) > 0);
        if (sat.length) {
            const satNames = sat.map(r => r.ev_name).filter(Boolean);
            const detailRe = /^(.+?)\s*[(（]\s*(?:내역|details|明细)\s*[:：]\s*(.+?)\s*[)）]$/i;
            const ms = satNames.map(n => n.match(detailRe));
            const mergeable = satNames.length > 1 && ms.every(m => m) && ms.every(m => m[1] === ms[0][1]);
            if (mergeable) {
                result.push(`${ms[0][1]}\n- ${ms.map(m => m[2].trim()).join('\n- ')}`);
            } else {
                satNames.forEach(n => result.push(n));
            }
        } else if (isBad) {
            const neg2 = subRows.find(r => r.score === 0 && r.formula
                && (r.formula.includes('result==\'모름\'') || r.formula.includes('result==\'아니오')));
            if (neg2 && neg2.ev_name) result.push(neg2.ev_name);
        }
    }
    return result;
}

export default {
    name: 'NaUpgradeExporter',
    data () {
        return {
            loading: false,
            hasRun: false,
            progress: {done: 0, total: 0},
            scannedCompanies: 0,
            results: [],
            errors: []
        };
    },
    computed: {
        targetCampaigns () {
            // 단일빌드: custom 은 앱별 env 하나만 봤지만 여기선 hyundai_v2 + hmc_theme 합집합을 순회한다.
            return [
                ...getRendererCampaignList('hyundai_v2'),
                ...getRendererCampaignList('hmc_theme'),
            ].filter(id => id !== 550); // 550 = hyundai-v2 테스트 캠페인
        },
        progressPercent () {
            return this.progress.total ? ((this.progress.done / this.progress.total) * 100).toFixed(1) : 0;
        }
    },
    methods: {
        // 한 기업의 sub_code → 답변 result(예/아니오/모름) 맵 조회. (report mixin loadAnswerResults 미러)
        async fetchAnswerResults (co, scoringRows) {
            try {
                const subCodes = [...new Set(scoringRows.map(r => r.sub_code).filter(Boolean))];
                if (!co.company_id || !co.campaign_id || !subCodes.length) return {};
                const res = await this.$axios.get('/subs', {
                    params: {
                        company_id: co.company_id,
                        company_name: co.company_name,
                        campaign_id: co.campaign_id,
                        sub_codes: subCodes.join(',')
                    }
                });
                return answerResultMapFromSubs(res?.data ?? []);
            } catch (e) {
                return {};
            }
        },
        // 한 기업의 scoring/risk 에서 NA 만점으로 등급이 상향된 Code 목록 반환.
        detectUpgrades (scoring_pd, risk_pd, campaignId) {
            const rawRows = [...(scoring_pd ?? []), ...(risk_pd ?? [])];
            if (!rawRows.length) return [];

            const cfgAll = code_level_thresholds[String(campaignId)] || {};
            const config = {};
            // B-2(그린워싱)는 codeLevelThresholds 와 동일하게 전면 제외.
            Object.keys(cfgAll).forEach(k => {
                if (!/^B-2/.test(k)) config[k] = cfgAll[k];
            });

            const c3Groups = {};
            for (const row of rawRows) {
                const c3name = row.category3_question_name;
                if (!c3name) continue;
                const m = String(c3name).match(/^([A-Z]-\d+-\d+)/);
                if (!m) continue;
                const codeId = m[1];
                if (!config[codeId]) continue;
                if (!c3Groups[codeId]) c3Groups[codeId] = [];
                c3Groups[codeId].push(row);
            }

            const upgrades = [];
            for (const codeId of Object.keys(config)) {
                const rows = c3Groups[codeId];
                if (!rows || !rows.length) continue;
                const items = buildItems(rows);
                if (!items.length) continue;
                if (!items.some(i => i.not_applicable)) continue; // NA 없으면 격상 불가

                const lv = config[codeId].levels || {};
                const levelWith = levelOf(items, lv, true);
                const levelWithout = levelOf(items, lv, false);
                if (levelWith === 'na' || levelWithout === 'na') continue;

                if (LEVEL_RANK[levelWith] > LEVEL_RANK[levelWithout]) {
                    const naItems = items.filter(i => i.not_applicable);
                    // 도출 문구 = 격상된(현재 표출) 등급의 설명 문구. code_localized.ko 우선, 없으면 config defaultMessage.
                    const derivedText = code_localized[codeId]?.levels?.[levelWith]?.ko
                        || lv[levelWith]?.defaultMessage || '';
                    upgrades.push({
                        code: codeId,
                        codeName: code_localized[codeId]?.name?.ko || config[codeId].name || '',
                        levelWithout,
                        levelWith,
                        derivedText,
                        qno: naItems.map(i => i.label).filter(Boolean).join(', '),
                        subCodes: naItems.map(i => i.sub_code).join(', ')
                    });
                }
            }
            return upgrades;
        },
        async run () {
            this.loading = true;
            this.hasRun = false;
            this.results = [];
            this.errors = [];
            this.scannedCompanies = 0;
            this.progress = {done: 0, total: 0};

            try {
                // 1) 대상 캠페인별 하위 기업 전수 수집 (점수 생성 여부와 무관하게 모두 — total_score 필터 없음)
                //    캠페인명·벤더코드·담당자메일은 results 응답에 이미 포함되어 그대로 사용.
                const companies = [];
                for (const campaign_id of this.targetCampaigns) {
                    try {
                        const res = await this.$axios.get('/assessments/results', {params: {campaign_id}});
                        const list = Array.isArray(res.data) ? res.data : [];
                        for (const c of list) {
                            const eff = c.priority ? c.priority : c;
                            if (eff.company_id == null) continue;
                            companies.push({
                                project_code: eff.project_code || c.project_code || '',
                                campaign_id: eff.campaign_id ?? c.campaign_id ?? campaign_id,
                                company_id: eff.company_id,
                                company_name: eff.company_name || c.company_name || '',
                                campaign_name: eff.campaign_name || c.campaign_name || '',
                                vendor_code: eff.vendor_code || c.vendor_code || '',
                                requested_e_mail: eff.requested_e_mail || c.requested_e_mail || ''
                            });
                        }
                    } catch (e) {
                        this.errors.push({scope: `캠페인 ${campaign_id}`, reason: '기업 목록 조회 실패'});
                    }
                }

                this.progress.total = companies.length;
                this.scannedCompanies = companies.length;

                // 2) 기업별 /report/data 순차 조회 + 격상 탐지 (live parser 부하 고려 — 동시 호출 금지)
                const rows = [];
                for (let i = 0; i < companies.length; i++) {
                    const co = companies[i];
                    try {
                        const res = await this.$axios.get('/report/data', {
                            params: {
                                project_code: co.project_code,
                                campaign_id: co.campaign_id,
                                company_id: co.company_id
                            }
                        });
                        const d = res.data || {};
                        const scoringRows = d?.scoring?.scoring_pd ?? [];
                        const riskRows = d?.risk?.risk_pd ?? [];
                        const ups = this.detectUpgrades(scoringRows, riskRows, co.campaign_id);

                        // 격상 코드가 있을 때만 답변 result(/subs) 1회 조회 → bullet 문구 계산 (보고서와 동일).
                        let answerMap = {};
                        if (ups.length) {
                            answerMap = await this.fetchAnswerResults(co, scoringRows);
                        }
                        const rawRows = [...scoringRows, ...riskRows];

                        for (const u of ups) {
                            const bullets = collectEvNames(u.code, rawRows, answerMap, u.levelWith);
                            rows.push({
                                '프로젝트코드': co.project_code,
                                '캠페인명': co.campaign_name,
                                '기업명': co.company_name,
                                '벤더코드': co.vendor_code,
                                '담당자메일': co.requested_e_mail,
                                '코드명': `${u.code}. ${u.codeName}`,
                                'Q no': u.qno,
                                'SubCode': u.subCodes,
                                '격상 유형': `${LEVEL_KO[u.levelWithout]} => ${LEVEL_KO[u.levelWith]}`,
                                '도출 문구': u.derivedText,
                                'Bullet 문구': bullets.join('\n'),
                                '변경문구': ''
                            });
                        }
                    } catch (e) {
                        // 평가 충돌(422) 포함 모든 조회 실패는 건너뜀 — 충돌은 수동 처리 영역이므로 별도 핸들링하지 않음.
                        this.errors.push({scope: `${co.project_code} / ${co.company_id}`, reason: 'report/data 조회 실패'});
                    } finally {
                        this.progress.done++;
                    }
                }

                // 코드명 → 프로젝트코드 → 기업명 순 정렬 (검토 편의)
                rows.sort((a, b) => {
                    if (a['코드명'] !== b['코드명']) return a['코드명'] < b['코드명'] ? -1 : 1;
                    if (a['프로젝트코드'] !== b['프로젝트코드']) return a['프로젝트코드'] < b['프로젝트코드'] ? -1 : 1;
                    return String(a['기업명']).localeCompare(String(b['기업명']));
                });
                this.results = rows;
                this.hasRun = true;

                // 3) xlsx 다운로드
                if (rows.length) {
                    this.downloadXlsx(rows);
                } else {
                    this.$showModal({
                        type: 'alert',
                        state: 'success',
                        title: '해당없음 격상 목록',
                        message: '격상된 케이스가 없습니다.'
                    });
                }
            } catch (error) {
                console.error('NaUpgradeExporter run error:', error);
                this.$showModal({
                    type: 'alert',
                    state: 'danger',
                    title: '해당없음 격상 목록',
                    message: '조회 중 오류가 발생했습니다. 개발팀에 문의해주세요.'
                });
            } finally {
                this.loading = false;
            }
        },
        downloadXlsx (rows) {
            const header = [
                '프로젝트코드', '캠페인명', '기업명', '벤더코드', '담당자메일',
                '코드명', 'Q no', 'SubCode', '격상 유형', '도출 문구', 'Bullet 문구', '변경문구'
            ];
            const ws = XLSX.utils.json_to_sheet(rows, {header});
            ws['!cols'] = [
                {wch: 16}, {wch: 22}, {wch: 24}, {wch: 10}, {wch: 26},
                {wch: 30}, {wch: 14}, {wch: 16}, {wch: 16}, {wch: 50}, {wch: 50}, {wch: 50}
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, '해당없음 격상');
            XLSX.writeFile(wb, `현대차_사업장ESG_해당없음격상목록_${dayjs().format('YYYYMMDD-HHmm')}.xlsx`);
        }
    }
};
</script>

<style scoped>
</style>
